import type { GitCommit } from "../chat/hooks/useReploAgentConnection";

import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { Ellipsis, GitCommit as GitCommitIcon, RotateCcw } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

export const VersionCardSkeleton = () => (
  <div className="flex justify-between p-3 rounded-xl gap-1.5 border border-border border-0.5">
    <div className="flex flex-col gap-1">
      <Skeleton className="max-w-58 w-full h-5" />
      <Skeleton className="max-w-18 w-full h-4" />
    </div>

    <div className="h-8 w-8" />
  </div>
);

// =============================================================================
// GitVersionCard - Version card for git commits
// =============================================================================

export function GitVersionCard({
  commit,
  isCurrentVersion,
  isPreviewing = false,
  onPreview,
  onRevert,
  isReverting,
  isLoadingPreview = false,
  className,
}: {
  commit: GitCommit;
  isCurrentVersion: boolean;
  isPreviewing?: boolean;
  onPreview?: () => void;
  onRevert: () => void;
  isReverting: boolean;
  isLoadingPreview?: boolean;
  className?: string;
}) {
  const [isHovering, setIsHovering] = useState(false);
  const isDebugMode = useIsDebugMode();

  const getBackgroundStyle = () => {
    if (isCurrentVersion || isPreviewing) {
      return "linear-gradient(white, white) padding-box, var(--color-gradient-ai-10) border-box";
    }

    return `${isHovering ? "linear-gradient(var(--color-sheet), var(--color-sheet))" : "linear-gradient(white, white)"} padding-box, var(--color-border) border-box`;
  };

  const getVersionSubtext = () => {
    if (isPreviewing) {
      return "Previewing this version";
    }

    if (isCurrentVersion) {
      return "Current Version";
    }

    return `${commit.author} · ${commit.relativeTime}`;
  };

  return (
    <div
      className={twMerge(
        "relative isolate flex items-start justify-between gap-1.5 p-3 rounded-xl border-transparent border-0.5 cursor-pointer max-w-full",
        className,
      )}
      style={{
        background: getBackgroundStyle(),
        maxWidth:
          isCurrentVersion || isPreviewing ? "calc(100% - 8px)" : undefined,
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={onPreview}
    >
      {/* Glow border for current version or previewing version */}
      {(isCurrentVersion || isPreviewing) && (
        <AnimatePresence>
          <motion.span
            aria-hidden
            className="pointer-events-none absolute -z-1 border-transparent bg-clip-padding-box"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              background: `${isHovering ? "linear-gradient(var(--color-sheet), var(--color-sheet))" : "linear-gradient(white, white)"} padding-box, var(--color-gradient-ai-10) border-box`,
              borderWidth: "4px",
              inset: "-4px",
              borderRadius: "16px",
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
          />
        </AnimatePresence>
      )}

      <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
        <span className="text-sm truncate block">{commit.message}</span>

        <span className="text-xs text-muted truncate block">
          {getVersionSubtext()}
        </span>
      </div>

      <div className="flex items-center gap-1">
        {isDebugMode && <GitCommitDebugMenu commitSha={commit.sha} />}
        {isLoadingPreview ? (
          <Button icon variant="tertiary" size="sm" loading disabled>
            <RotateCcw />
          </Button>
        ) : (
          <Tooltip
            content={
              isCurrentVersion
                ? "You are already on this version"
                : "Restore to this version"
            }
          >
            <Button
              icon
              onClick={(e) => {
                e.stopPropagation();
                onRevert();
              }}
              loading={isReverting}
              variant="tertiary"
              size="sm"
              disabled={isCurrentVersion || isReverting}
            >
              <RotateCcw />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function GitCommitDebugMenu({ commitSha }: { commitSha: string }) {
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Ellipsis />
          </Button>
        }
      />
      <MenuContent align="end" side="bottom">
        <MenuItem
          label={`Copy SHA: ${commitSha.slice(0, 7)}`}
          startEnhancer={<GitCommitIcon />}
          onClick={() => {
            void navigator.clipboard.writeText(commitSha);
          }}
        />
      </MenuContent>
    </Menu>
  );
}
